import { type Feature, FEATURE_SET } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Path Resolution
// ─────────────────────────────────────────────────────────────────────────────
//
// Maps a URL pathname to a Feature (for feature routes) or a settings section
// string (for standalone settings). The hook then checks the resolved value
// against scope policy allowlists.
//
// This file does NOT decide allow/block — it only resolves identity.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * URL path segments whose name differs from the Feature they belong to.
 *
 * Used for both root-level paths (e.g. /bulk → 'marketing') and settings
 * sub-paths (e.g. /settings/phone → 'calls').
 *
 * If a URL segment already matches a Feature name exactly (e.g. /fax → 'fax'),
 * no alias is needed — the resolver falls through to FEATURE_SET.has().
 */
const PATH_ALIASES: Record<string, Feature> = {
  bulk: 'marketing', // /bulk/* is the bulk-messaging section
  phone: 'calls', // /settings/phone → 'calls' feature
  messaging: 'messages', // /settings/messaging → 'messages' feature
  'digital-forms': 'forms', // /settings/digital-forms → 'forms' feature
  email: 'marketing', // /settings/email → 'marketing' feature
};

/**
 * Result of path resolution. Discriminated union so the hook knows
 * which resolver path to use.
 */
export type PathResolution =
  | { type: 'feature'; feature: Feature }
  | { type: 'settings'; section: string }
  | { type: 'unresolved' };

/**
 * Resolves a URL pathname to a typed PathResolution.
 *
 * Resolution strategy:
 *
 * 1. **Root '/'** → Feature 'home'
 *
 * 2. **Settings paths (/settings/<section>):**
 *    Settings is deny-by-default — everything inside /settings/* is considered
 *    "our business" (unlike main routes where unknown paths are ignored).
 *    - Bare /settings (no section) → unresolved (the modal shell, no policy)
 *    - Section resolves to a feature via alias or direct match → { type: 'feature' }
 *      (inherits parent feature's allow/block decision)
 *    - Section doesn't match any feature → { type: 'settings', section }
 *      (checked against the standalone settings allowlist)
 *
 * 3. **Root-level paths:**
 *    - Alias match → { type: 'feature', feature: aliased }
 *    - Direct FEATURE_SET match → { type: 'feature', feature: segment }
 *    - No match → unresolved (not a feature route, e.g. /auth/login — not our business)
 */
export const resolvePath = (pathname: string): PathResolution => {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = normalized.split('/').filter(Boolean);
  const root = segments[0];

  // Empty path = dashboard/home
  if (!root) return { type: 'feature', feature: 'home' };

  // Settings: deny-by-default for all sections
  if (root === 'settings') {
    const section = segments[1];

    // Bare /settings — the modal shell, no policy applies
    if (!section) return { type: 'unresolved' };

    // Try resolving section to its parent feature (via alias or direct match)
    const feature = PATH_ALIASES[section] ?? section;
    if (FEATURE_SET.has(feature)) {
      return { type: 'feature', feature: feature as Feature };
    }

    // Not a feature — standalone settings section (checked against ALLOWED_SETTINGS)
    return { type: 'settings', section };
  }

  // Root-level paths: check aliases, then direct feature match
  if (PATH_ALIASES[root]) {
    return { type: 'feature', feature: PATH_ALIASES[root] };
  }

  if (FEATURE_SET.has(root)) {
    return { type: 'feature', feature: root as Feature };
  }

  // Unknown path — not a feature, not our business (e.g. /auth/login)
  return { type: 'unresolved' };
};
